import { useInfiniteQuery, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import {
  LimitedSchemaInfiniteQueryOptions,
  LimitedSchemaFetchInfiniteQueryOptions,
} from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetAppointmentLocationsViewIO } from '../types';

export const getGetAppointmentLocationsViewQueryKey = (req: GetAppointmentLocationsViewIO['input']) =>
  ['AnalyticsGatewayV1', 'GetAppointmentLocationsView', req] as const;
type QueryKey = ReturnType<typeof getGetAppointmentLocationsViewQueryKey>;
// type QueryFnArgs = QueryFunctionContext<QueryKey, GetAppointmentLocationsViewPageParams>;

type GetAppointmentLocationsViewPageParams = {
  // TODO: add type for GetAppointmentLocationsView page parameters here
};
/**
 * A hook that returns an infinite query for the `GetAppointmentLocationsView` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentLocationsViewQuery = <E = unknown, D = GetAppointmentLocationsViewIO['output']>(
  req: GetAppointmentLocationsViewIO['input'],
  options?: Omit<
    LimitedSchemaInfiniteQueryOptions<GetAppointmentLocationsViewIO, E, D, QueryKey>,
    'getNextPageParam' | 'getPreviousPageParam'
  >,
  httpOptions?: Options
) =>
  useInfiniteQuery<GetAppointmentLocationsViewIO['output'], E, D, QueryKey>({
    queryKey: getGetAppointmentLocationsViewQueryKey(req),
    queryFn: () => {
      return SchemaAnalyticsGateway1Service.GetAppointmentLocationsView(
        {
          ...req,
          // TODO: override parts of the request based on page params here
        },
        httpOptions
      );
    },
    ...options,
    getNextPageParam: (_lastPage, allPages): GetAppointmentLocationsViewPageParams => {
      // TODO: calculate nextPageParam and return it here
      return allPages.length;
    },
    getPreviousPageParam: (_firstPage, allPages): GetAppointmentLocationsViewPageParams => {
      // TODO: calculate nextPageParam and return it here
      return allPages.length;
    },
  });

/**
 * A hook that returns a function to fetch the query for the `GetAppointmentLocationsView` endpoint.
 * It uses `queryClient.fetchInfiniteQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useInfiniteQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetAppointmentLocationsView = () => {
  const queryClient = useQueryClient();

  const fetchGetAppointmentLocationsView = async <E = unknown, D = GetAppointmentLocationsViewIO['output']>(
    req: GetAppointmentLocationsViewIO['input'],
    options?: LimitedSchemaFetchInfiniteQueryOptions<GetAppointmentLocationsViewIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchInfiniteQuery(
      getGetAppointmentLocationsViewQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetAppointmentLocationsView(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetAppointmentLocationsView;
};
